import { computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';

// The ERP customer code. `identifier` is NOT always one: the comments pipeline
// writes the raw Facebook PSID there, and the comment->DM mirror reuses that
// contact for a real Messenger thread — so an unguarded fallback marks a random
// commenter as a registered customer. An ERP Customer docname is always
// CUST-###### (live Document Naming Rule; 26,773 of 26,773 customers conformed on
// a production copy, 2026-08-19). Same regex as custom_addons/customer/customer.py.
//
// 🚨 Exported, not re-declared per file: two branches once added this same
// constant to ConversationHeader on the same day, git merged both hunks cleanly,
// and the duplicate `const` only blew up in assets:precompile.
export const ERP_CUSTOMER_CODE = /^CUST-\d+$/;

// Module scope on purpose: every row of the conversation list builds this chip,
// so rebuilding the map inside the computed would allocate it per card per
// refresh. Only the matched label is translated.
//
// Two weights of the SAME hue. The hue is the meaning — «مدرس» بنفسجي في
// المكانين — وبيتقرر هنا مرة واحدة. علوّ الصوت بس هو اللي بيفرق:
//
//   soft   — الهيدر. الشريحة هناك لوحدها في سطر واسع وواضحة أصلاً.
//   strong — القايمة. طلب المالك ٢٥/٨: «محتاج التصنيف في القائمة يكون أوضح
//            شوية وجذاب، وبألوان واضحة أكتر وساطعة». صف الكارت مزدحم ورمادي،
//            فالشريحة الباهتة بتضيع فيه.
//
// 🚨 الدرجات مقيسة مش متسايبة: خلفية ١١ + نص أبيض في الفاتح، والنص بيتقلب
// لدرجة ١ في الغامق لأن درجة ١١ نفسها بتبقى لون فاتح هناك. القراءات (عتبة
// AA = 4.5):
//
//   بنفسجي 5.94/7.48 · أزرق 4.91/9.03 · أصفر 4.61/12.18 · تركوازي 4.56/10.15
//
// 🚨 ماتستبدلش `text-white` بـ`text-n-{hue}-1` في الفاتح «عشان التماثل»:
// التركوازي بينزل من 4.56 لـ 4.49، يعني تحت العتبة بالظبط.
// 🚨 والأصفر المصمت الساطع (درجة ٩ + نص ١٢) بيدي 7.21 في الفاتح بس 1.30 في
// الغامق — نص مايتقراش لأي حد شغال على الثيم الغامق.
// ⚠️ درجة ١ أصلاً توكن **خلفية** مش نص؛ مستعملة هنا كنص فوق تعبئة مصمتة في
// الغامق بس. لو حد عدّل درجة ١ لأي سبب خاص بالخلفيات، لازم يعيد قياس دول.
//
// موجودة نسخة تالتة من نفس القايمة في CloseDispositionModal.vue (TONES[hue].on
// = `bg-n-{hue}-9 text-white`). ما اتعادش استعمالها هنا لأنها بتقع على الأصفر:
// `bg-n-amber-9 text-white` = ~1.7 — يعني أزرار المودال نفسها مش مقروءة وهي
// مختارة. لو حد وحّدهم بعدين، دي الدرجات اللي تتنقل مش العكس.
const CLASSIFICATION_STYLES = {
  __proto__: null,
  Student: {
    icon: 'i-lucide-backpack',
    soft: 'bg-n-blue-3 text-n-blue-11',
    strong: 'bg-n-blue-11 text-white dark:text-n-blue-1',
  },
  Teacher: {
    icon: 'i-lucide-graduation-cap',
    soft: 'bg-n-violet-3 text-n-violet-11',
    strong: 'bg-n-violet-11 text-white dark:text-n-violet-1',
    // تفصيلة جوّه نفس الهوية (مواد المدرس في الهيدر): نفس الخلفية، بس نص درجة
    // ١٢ عشان تتقري لوحدها وسط صف الأزرار — درجة ١١ بتدي ٥.٣ وهو **أقل** من
    // الرمادي اللي كانت عليه (٥.٧٩)، فكانت هتبقى أوضح في الاسم وأخفى في الواقع.
    detail: 'bg-n-violet-3 text-n-violet-12',
  },
  Library: {
    icon: 'i-lucide-store',
    soft: 'bg-n-amber-3 text-n-amber-11',
    strong: 'bg-n-amber-11 text-white dark:text-n-amber-1',
  },
  Center: {
    icon: 'i-lucide-school',
    soft: 'bg-n-teal-3 text-n-teal-11',
    strong: 'bg-n-teal-11 text-white dark:text-n-teal-1',
  },
  // 🚨 «عميل آخر» بيفضل هادي في المكانين عن قصد، وهو الوحيد الخارج عن قاعدة
  // الدرجات فوق. الشريحة اتحطت أصلاً عشان المالك يلمح المدرس والمكتبة وسط
  // الطلبة؛ ولو التصنيف اللي **مابيقولش حاجة** بقى أصرخ واحد في الصف (رمادي
  // مصمت بيدي تباين ٨.٧٧ على خلفية الكارت في الغامق، أعلى من البنفسجي نفسه)
  // يبقى الترتيب اتقلب. لو اتزاد تصنيف سادس ليه معنى، ياخد قاعدة الدرجات فوق.
  Other: {
    soft: 'bg-n-alpha-2 text-n-slate-11',
    strong: 'bg-n-alpha-2 text-n-slate-11',
  },
};

// الأيقونة بتاعة كل تصنيف عايشة في نفس الماب مع ألوانه عن قصد: الشريحة والصورة
// لازم يتفقوا على نفس اللون، ولو الاتنين في مابين منفصلين أول تعديل على واحد
// بيخلّي صف واحد يقول لونين مختلفين لنفس العميل.
//
// صورة العميل في القايمة. ٪٤٢ من الصفوف اللي من غير صورة بتاعة عميل مصنّف،
// والباقي مش معروف عندنا أصلاً. القياس (٣٠ يوم على نسخة من الداتا الحقيقية):
// ٦٬٦٦٢ صف بلا صورة ومصنّف · ٩٬٣٦١ بلا صورة ومش مربوط · ٣ بس مربوطين بلا تصنيف.
//
// فالقاعدة: المصنّف بياخد أيقونة تصنيفه بلون تصنيفه، واللي مش مصنّف يفضل على
// الحرفين زي ما هو. وده بيدي فرق مقروء بنظرة: **أيقونة = عميل نعرفه، حرفين =
// لسه مش معروف**.
//
// 🚨 اللي عنده صورة حقيقية بتفضل صورته — Avatar بيرتّب الأولوية بنفسه.
// اتطلبت حلقة ملونة حوالين الصورة و**ما اتعملتش**: القياس بيقول إن مفيش ولا
// عميل مصنّف عنده صورة (٢١٬٥٧١ صف بصور، كلهم فيسبوك وإنستجرام ومش مربوطين
// بالـERP)، وعلى الأيقونة نفسها حلقة بنفس لون التعبئة مش بتتشاف. يعني ستايل
// مايظهرش أبدًا. لو الربط اتوسّع للفيسبوك، دي الحتة اللي تترجع فيها.

// Spelled out rather than interpolated so the keys stay greppable — that is what
// the no-dynamic-keys lint is protecting, and a renamed key should break here.
const classificationLabel = (t, classification) => {
  switch (classification) {
    case 'Student':
      return t('CONVERSATION.HEADER.ERP_CLASSIFICATIONS.STUDENT');
    case 'Teacher':
      return t('CONVERSATION.HEADER.ERP_CLASSIFICATIONS.TEACHER');
    case 'Library':
      return t('CONVERSATION.HEADER.ERP_CLASSIFICATIONS.LIBRARY');
    case 'Center':
      return t('CONVERSATION.HEADER.ERP_CLASSIFICATIONS.CENTER');
    case 'Other':
      return t('CONVERSATION.HEADER.ERP_CLASSIFICATIONS.OTHER');
    default:
      return '';
  }
};

/**
 * Eltafouk: the two things an agent needs to know about a contact before they
 * read a single word of the message — «هو مين» و«هو عندنا ولا لأ».
 *
 * Lives in one place because it is shown in two: the conversation header and
 * every row of the conversation list. Owner's ask (٢٤/٨) was explicitly "زي ما
 * بيحصل في رأس نافذة المحادثة" — نفس الكلمات ونفس الألوان، فاللون نفسه يبقى
 * مقروء بنظرة. نسخة من الماب في كل كومبوننت كانت هتفرق أول ما حد يلمس واحد.
 *
 * الوزن (soft/strong) هو الحاجة الوحيدة اللي بتفرق بين المكانين، بطلب المالك
 * ٢٥/٨ إن شريحة القايمة تبقى أسطع — واللون، اللي هو المعنى، بيفضل واحد.
 *
 * Reads two shapes, in priority order:
 *   · `additional_attributes` — the full contact, mirrored from the ERP Customer
 *     by the bridge. Present once contacts/show has loaded (header, and any card
 *     whose conversation has been opened).
 *   · `erp_identity` — the three-field projection the conversation-list payload
 *     ships instead (see the slim sender in _conversation.json.jbuilder). It is
 *     deliberately NOT written into additional_attributes: setActiveChat treats
 *     an absent additional_attributes as "this record is still slim, fetch the
 *     full contact", and filling it with a partial copy would silently disable
 *     that fetch.
 *
 * Full data wins wherever both are present, so a record can only get fresher.
 *
 * @param {import('vue').Ref<Object>|Object} contact reactive contact record
 */
export function useErpCustomerIdentity(contact) {
  const { t } = useI18n();

  const identity = computed(() => {
    const record = unref(contact) || {};
    const attributes = record.additional_attributes;
    if (attributes) {
      return {
        classification: attributes.custom_customer_classification,
        linkStatus: attributes.erp_link_status,
        customer: attributes.erp_customer || record.identifier || '',
      };
    }
    const slim = record.erp_identity || {};
    return {
      classification: slim.classification,
      linkStatus: slim.link_status,
      customer: slim.customer || '',
    };
  });

  // 🚨 The classification chip answers "who am I talking to" — a teacher gets a
  // different tone from a student. It is NOT the same question as the check
  // below: an unclassified contact can still be a registered customer.
  // 🚨 الماب فوق `__proto__: null` عن قصد: من غيره تصنيف اسمه `constructor` أو
  // `toString` كان بيعدّي الشرط ده (لأنه موروث من Object.prototype وقيمته
  // truthy) وبعدين يطلع شريحة فاضية من غير نص ولا لون — مجرد فراغ قبل الاسم.
  // والقيمة دي مش نظرية: contacts_controller بيسمح بـadditional_attributes
  // كاملة من الـAPI، واستيراد جهات الاتصال بيكتب التصنيف زي ما هو من الـCSV.
  const classificationChip = computed(() => {
    const { classification } = identity.value;
    const style = CLASSIFICATION_STYLES[classification];
    if (!style) return null;
    return {
      label: classificationLabel(t, classification),
      // بيقع على الهادية لو التصنيف مالوش تفصيلة خاصة.
      detailClass: style.detail || style.soft,
      // مسمّيين بالوزن مش بـ`class` سايبة: اللي هياخد الشريحة لمكان تالت لازم
      // يقول هو عايز الهادية ولا العالية، مايقعش على الافتراضي من غير ما يعرف.
      softClass: style.soft,
      strongClass: style.strong,
    };
  });

  const erpCustomer = computed(() =>
    ERP_CUSTOMER_CODE.test(identity.value.customer)
      ? identity.value.customer
      : ''
  );

  // The blue check — «عميل مسجّل عندنا». Linked status alone is not enough:
  // without a code there is nothing to open in ERP, so the two travel together
  // exactly as they do in the header.
  const isErpLinked = computed(
    () => identity.value.linkStatus === 'linked' && !!erpCustomer.value
  );

  // بترجع null للمش مصنّف عشان الكارت يسيب الأفاتار الافتراضي (الحرفين) زي ما هو.
  // الصورة: نفس أيقونة التصنيف بنفس لونه العالي. بترجع null للمش مصنّف عشان
  // الكارت يسيب الأفاتار الافتراضي (الحرفين) زي ما هو.
  const classificationAvatar = computed(() => {
    const style = CLASSIFICATION_STYLES[identity.value.classification];
    if (!style?.icon) return null;
    return { icon: style.icon, colorClass: style.strong };
  });

  return {
    classificationChip,
    classificationAvatar,
    erpCustomer,
    isErpLinked,
  };
}
